import { useState, useEffect, useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";

import qs from "query-string";
import api from "services/api";
import toast from "react-hot-toast";

import defaultStyleImage from "assets/images/upstyled/default_background.png";
import AILab from "./ailab";
import AILabCard from "./ailabcard";

// import { Gallery } from "react-grid-gallery";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import { moodboardSortOptions } from "utils/data";
import { compare } from "utils/helper";
import { MAX_SCREEN_WIDTH } from "utils/data";

const AILabList = ({ sharedView = false, create = false }) => {
  const [ailabs, setAILabs] = useState([]);
  const [filteredAILabs, setFilteredAILabs] = useState([]);
  const [orderOpt, setOrderOpt] = useState(moodboardSortOptions[0]);
  const [searchStr, setSearchStr] = useState("");

  const [index, setIndex] = useState(-1);

  const location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const ailab_id = urlObj?.query?.id;
  const aiTab = urlObj?.query?.tab;

  const navigate = useNavigate();

  const currentImage = ailabs[index];
  const nextIndex = (index + 1) % ailabs.length;
  const nextImage = ailabs[nextIndex] || currentImage;
  const prevIndex = (index + ailabs.length - 1) % ailabs.length;
  const prevImage = ailabs[prevIndex] || currentImage;

  const handleClick = (index, item) => setIndex(index);
  const handleClose = () => setIndex(-1);
  const handleMovePrev = () => setIndex(prevIndex);
  const handleMoveNext = () => setIndex(nextIndex);

  useEffect(() => {
    retrieveAILabs();
  }, [url]);

  useEffect(() => {
    if (create) {
      handleAddAILab();
    }
  }, [create]);

  const retrieveAILabs = async () => {
    try {
      const { data } = await api.ailab.list();
      let myAILab = data?.ailabs || [];
      myAILab = myAILab.sort((a, b) => compare(a, b, "-updated_at"));

      setAILabs(myAILab);
      setFilteredAILabs(myAILab);
    } catch (err) {
      toast.error(`Retrieve ailabs for collection:${detail["title"]} failed!`);
      console.log(err);
    }
  };

  const handleOpenAILab = (ailab) => {
    if (ailab?.id) {
      navigate(`/workspace/ailab?id=${ailab.id}${aiTab ? `&tab=${aiTab}` : ""}`);
    }
  };

  const handleFilterCollections = (str = searchStr) => {
    const copyAILabs = [...ailabs];
    let filteredList = copyAILabs; //all projects

    if (str) {
      const strUpper = str.toUpperCase();
      const searchFields = ["name"];
      filteredList = filteredList.filter((x) => {
        let result = false;
        for (let i = 0; i < searchFields.length; i++) {
          if (x[searchFields[i]]) {
            if (typeof x[searchFields[i]] == "string") {
              if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                result = true;
              }
            } else if (typeof x[searchFields[i]] == "object") {
              const text = JSON.stringify(x[searchFields[i]]);
              if (text.toUpperCase().includes(strUpper)) {
                result = true;
              }
            }
          }
        }
        return result;
      });
    }
    setFilteredAILabs(filteredList);
  };

  const handleSearchChange = (str) => {
    let formattedStr = "";
    if (str) {
      formattedStr = str.trim();
    }
    setSearchStr(formattedStr);
    handleFilterCollections(formattedStr);
  };

  const handleOnSortChange = (e) => {
    setOrderOpt(e);
    let sortStr = e?.value;
    const newFilteredList = filteredAILabs.sort((a, b) => compare(a, b, sortStr));
    setFilteredAILabs(newFilteredList);
  };

  const handleAddAILab = async () => {
    try {
      let payload = {
        name: "Untitled",
      };
      //create ailab
      const newAILab = await api.ailab.save(payload);
      const ailab_id = newAILab?.data?.id;

      toast.success(`Your AILab has been successfully created!`);
      navigate(`/workspace/ailab?id=${ailab_id}${aiTab ? `&tab=${aiTab}` : ""}`, { replace: true });
    } catch (err) {
      toast.error(`Your AILab could not be created!`);
      console.log(err);
    }
  };

  if (ailab_id) {
    return <AILab ailab_id={ailab_id} />;
  } else {
    return (
      <SuiBox
        my={1}
        sx={{
          maxWidth: MAX_SCREEN_WIDTH,
          margin: "auto",
          minHeight: "80vh",
          px: 3,
        }}
      >
        <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} mr={1}>
          <SuiButton
            variant="gradient"
            color={"dark"}
            onClick={() => {
              handleAddAILab();
            }}
          >
            Create New AILab
          </SuiButton>

          <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
            <SuiBox>
              <Card sx={{ borderRadius: 2, width: 200, boxShadow: "none" }}>
                <SuiSelect
                  options={moodboardSortOptions}
                  placeholder="Sort"
                  isClearable
                  onChange={handleOnSortChange}
                  value={orderOpt}
                />
              </Card>
            </SuiBox>
            <SuiBox>
              <SuiInput
                placeholder="Search Keywords"
                type="text"
                onChange={(e) => handleSearchChange(e?.target?.value)}
                icon={{
                  direction: "right",
                  component: (
                    <SearchIcon
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleSearchChange(searchStr)}
                    />
                  ),
                }}
              />{" "}
            </SuiBox>
          </SuiBox>
        </SuiBox>

        <div>
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 576: 2, 768: 3, 992: 4, 1200: 4, 1400: 4 }}
          >
            <Masonry>
              {filteredAILabs.map((x) => (
                <AILabCard
                  handleAILabClick={handleOpenAILab}
                  ailab={x}
                  handleRefresh={retrieveAILabs}
                  imgOptions={{
                    overflow: "hidden",
                    maxHeight: 400,
                  }}
                />
              ))}
            </Masonry>
          </ResponsiveMasonry>

          {!!currentImage && (
            /* @ts-ignore */
            <Lightbox
              mainSrc={currentImage.original}
              imageTitle={currentImage.caption}
              mainSrcThumbnail={currentImage.src}
              nextSrc={nextImage.original}
              nextSrcThumbnail={nextImage.src}
              prevSrc={prevImage.original}
              prevSrcThumbnail={prevImage.src}
              onCloseRequest={handleClose}
              onMovePrevRequest={handleMovePrev}
              onMoveNextRequest={handleMoveNext}
            />
          )}
        </div>
      </SuiBox>
    );
  }
};

export default AILabList;
